/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { isNull } from 'es-toolkit';
import { useParams } from 'react-router-dom';

import {
  ClientTeamPaymentState,
  ClientTeamSetStripeDefaultPaymentMethodDocument,
  TeamPaymentBillingAmountAndDateDocument,
  TeamPaymentCardsDocument,
  TeamPaymentDocument,
} from '@fai/friendli-suite/generated/client/graphql';

export const usePaymentState = (teamId: string) => {
  const {
    data: rawData,
    loading,
    error,
  } = useQuery(TeamPaymentDocument, {
    variables: { teamId },
    fetchPolicy: 'cache-and-network',
  });

  const payment = rawData?.clientSession?.user.team?.team.payment;
  const isPaymentInvalid = payment?.state !== ClientTeamPaymentState.Valid;
  const isPaymentEmpty = isNull(payment) || payment?.state === ClientTeamPaymentState.Empty;
  const isPaymentDeclined = payment?.state === ClientTeamPaymentState.Declined;

  return {
    isPaymentInvalid,
    isPaymentEmpty,
    isPaymentDeclined,
    loading,
    error,
  };
};

export const usePaymentAmountAndDate = () => {
  const { teamId = '' } = useParams();
  const { data, loading } = useQuery(TeamPaymentBillingAmountAndDateDocument, {
    fetchPolicy: 'cache-and-network',
    variables: { clientTeamId: teamId },
  });

  const _data = data?.clientTeam?.billing;
  const monthlyPayment = _data?.monthlyPayment;
  const nextPaymentDate = _data?.nextPaymentDate ? dayjs(_data?.nextPaymentDate) : null;

  return { monthlyPayment, nextPaymentDate, loading };
};

export const usePaymentCards = (teamId: string) => {
  const { data, loading: cardsLoading } = useQuery(TeamPaymentCardsDocument, {
    variables: { teamId },
    fetchPolicy: 'cache-and-network',
  });

  const cards =
    data?.clientTeam?.billing?.stripe?.paymentMethods?.edges.map(({ node }) => ({
      brand: node.card?.brand,
      last4: node.card?.last4,
      expYear: node.card?.expYear,
      expMonth: node.card?.expMonth,
      isDefault: node.isDefault,
      paymentMethodId: node.id,
      updatedAt: node.updatedAt ? dayjs(node.updatedAt) : null,
    })) || [];

  return { cards, cardsLoading };
};

export const useSetDefaultPaymentCard = (teamId: string) => {
  const [setDefaultPaymentCardMutation] = useMutation(
    ClientTeamSetStripeDefaultPaymentMethodDocument,
    {
      refetchQueries: [
        { query: TeamPaymentCardsDocument, variables: { teamId } },
        { query: TeamPaymentDocument, variables: { teamId } },
      ],
    }
  );

  const onSetDefaultPaymentCard = (paymentMethodId: string) =>
    setDefaultPaymentCardMutation({
      variables: {
        input: {
          teamId,
          paymentMethodId,
        },
      },
    });

  return { onSetDefaultPaymentCard };
};
